import PremiumStatusDialog from "../../common/premium/PremiumStatusDialog";
import PremiumExportProgress from "../../common/premium/PremiumExportProgress";
import { createWarehousePreferenceState } from "./ordersComponents/orderWarehouseState";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router";
import styled from "styled-components";
import { AppQueryClient } from "../../../api/queryClient";
import { APP_TITLE } from "../../../config/constant";
import PopupContext from "../../../PopupContext";
import { getSpanWithColor } from "../../../utils/CardUtils";
import { formatDefaultDateTime, formatPrice } from "../../../utils/Formatter";
import * as Library from "../../../utils/Library";
import InfinityGridOrdersCards from "../../common/cardContainers/InfinityGridOrdersCards";
import { AlertIcon } from "../../common/dialogs/DialogBoxComponent";
import ListComponent from "../../common/lists/ListComponent";
import FullScreenLoader from "../../common/loaders/FullScreenLoader";
import MainWindowHeader, { CustomSearch } from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import FullScreenOverlay from "../../common/overlay/FullScreenOverlay";
import PremiumDialog from "../../common/premium/PremiumDialog";
import PremiumHeaderButton from "../../common/premium/PremiumHeaderButton";
import OrderMissingSizesTooltip from "./ordersComponents/OrderMissingSizesTooltip";
import OrderPreview from "./ordersComponents/OrderPreview";
import FrontSystemsChangePopup, { normalizeFrontSystemsCheck } from "./ordersComponents/FrontSystemsChangePopup";
import { canOpenSaved } from "./ordersComponents/automaticCheckState";
import { hasUnresolvedOrderAction as isChecking, canStartManualCheck } from "./ordersComponents/manualCheckState";
import { useQueryClient } from "react-query";
import { v4 as uuidv4 } from "uuid";
import { manualReportKey, manualReportContext, readManualReport, savedReportReview, retainSavedReport } from "./ordersComponents/manualSavedReport";
import { runFullOrderCheck } from "./ordersComponents/fullOrderCheck";

// #region SCOPED CSS
const StyledPage = styled.div`
    & {
        overflow-y: hidden;
        height: 100%;
        margin-right: -23px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 24px;
    }
`;

// #endregion

// #region CONSTANTS
const DEFAULT_STATUSES_FILTER = [
    { name: "Processing", value: "Processing", selected: true },
    { name: "Ready to export", value: "New", selected: true },
    { name: "Failed to export", value: "ExportFailedByRetailBrand", selected: true },
    //---------------
    { name: "Pending rules", value: "Pending rules", selected: true, hideFromOrganize: true },
    { name: "Pending master data", value: "Pending master data", selected: true, hideFromOrganize: true },
    {
        name: "Missing sizes in size system",
        value: "Missing sizes in size system",
        selected: true,
        hideFromOrganize: true
    },
    {
        name: "Missing info",
        value: "Missing info",
        selected: true,
        hideFromOrganize: true
    },
    {
        name: "Exported",
        value: "ExportedByRetailBrand",
        selected: true,
        hideFromOrganize: true
    }
];

const ORDERS_STATUSES = DEFAULT_STATUSES_FILTER.filter(item => !item.hideFromOrganize);

export const orderStatusColors = [
    {
        colorClass: "text-blue-600",
        forValues: ["Exported"]
    },
    {
        colorClass: "text-green-500",
        forValues: ["Ready to export"]
    },
    {
        colorClass: "text-orange-500",
        forValues: [
            "Pending rules",
            "Pending master data",
            "Missing sizes in size system",
            "Missing info",
            "Processing"
        ]
    },
    {
        colorClass: "text-red-500",
        forValues: ["Failed to export", "Deleted"]
    },
    {
        colorClass: "text-gray-300",
        forValues: ["--"]
    }
];

export const ButtonsEnabledRules = (buttonName, status) => {
    const mappedStatus = getMappedStatus(status)?.toLowerCase();

    switch (buttonName?.toLowerCase()) {
        case "check":
            return mappedStatus === "ready to export";
        case "export":
            if (mappedStatus === "missing info") return false;
            if (mappedStatus === "processing") return false;
            return mappedStatus === "ready to export" || mappedStatus === "failed to export";
        case "rules":
            //Keep RULES button enabled in all order status.
            if (!mappedStatus || mappedStatus === "--") return false;
            return true;
        case "delete":
        case "view":
            return true;
        default:
            return false;
    }
};
// #endregion

// #region CHECK TRANSPORT
const checkWarehouseID = value => typeof value === "string" ? value : Number.isSafeInteger(value) ? String(value) : undefined;
export async function requestAuthorizedFrontSystemsCheck(selection) {
    const response = await Library.makeApiRequest({
        endpoint: "X/SupplierOrders/checkSupplierOrderPrices",
        data: { supplierOrderID: selection.orderID, retailBrandID: selection.retailBrandID, storeID: selection.storeID,
            ...(selection.scopeKind === "order-check/v2" ? { scopeKind: "order-check/v2" } : { warehouseID: checkWarehouseID(selection.warehouseID) }), expectedUpdatedAt: selection.expectedUpdatedAt,
            ...(selection.reportContract ? { reportContract: selection.reportContract, reportRunID: selection.reportRunID } : {}),
            checkAction: selection.checkAction || "start", ...(selection.continuationToken ? { continuationToken: selection.continuationToken } : {}),
            ...(selection.cancelToken ? { cancelToken: selection.cancelToken } : {}) },
        onErrorShowMsg: false
    });
    return response?.data?.status === 200 ? response.data.data : undefined;
}
// #endregion
// #region DESTINATION PREFERENCE STATE
// Editing is separate from export readiness: a failed-export retry can use a saved
// default, while only an effectively Ready card can change the store preference.
export function canEditDestinationWarehouse(row, interactionBlocked = false) {
    return !interactionBlocked && !isChecking(row) && getMappedStatus(row?.status) === "Ready to export" &&
        row?.retailBrandPosStatus === "Connected" && row?.storePosStatus === "Connected";
}

export { createWarehousePreferenceState } from "./ordersComponents/orderWarehouseState";
// #endregion

export default function OrdersPage({ requestFrontSystemsCheck = requestAuthorizedFrontSystemsCheck } = {}) {
    const manualQueryClient = useQueryClient();
    document.title = `${APP_TITLE} - Orders`;

    // #region VARS
    const { MainLayoutProps, layoutType } = useOutletContext();

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };

    const [listNumRows, setListNumRows] = useState(10);
    const [viewLayoutStyle, setViewLayoutStyle] = useState("Grid");
    const gridRef = useRef(null);
    const { setGenericConfirmationPopup } = useContext(PopupContext);
    const navigate = useNavigate();
    // #endregion

    // #region STATES
    const [isLoading, setIsLoading] = useState(false);
    const [frontSystemsReview, setFrontSystemsReview] = useState(null);
    const [exportConfirmation, setExportConfirmation] = useState(null);
    const exportConfirmationRef = useRef(null);
    const [savedCheckReview, setSavedCheckReview] = useState(null);
    const savedViewerGeneration = useRef(0);
    const automaticRowsRef = useRef(new Map());
    const checkSessionRef = useRef(null);
    const exportWorkflowRef = useRef(null);
    const exportActionsRef = useRef(new Map());
    const [exportOutcomes, setExportOutcomes] = useState([]);
    const exportAction = row => exportActionsRef.current.get(warehouseStateRef.current.state(row).orderKey);
    const exportBlocked = row => ["preparing", "confirmation", "exporting", "unknown", "succeeded"].includes(exportAction(row)?.state);
    const setExportAction = (row, action) => {
        exportActionsRef.current.set(action.session?.orderKey || warehouseStateRef.current.state(row).orderKey, action);
        renderWarehouseState(value => value + 1);
    };
    const [searchText, setSearchText] = useState("");
    const [allRetailBrandsList, setAllRetailBrandsList] = useState([]);
    const [filteredRetailBrand, setFilteredRetailBrand] = useState([]);
    const [allLabelBrandsList, setAllLabelBrandsList] = useState([]);
    const [filteredLabelBrand, setFilteredLabelBrand] = useState([]);
    const [allStatusList, setAllStatusList] = useState(() => ORDERS_STATUSES.map(item => ({ ...item })));
    const [filteredStatus, setFilteredStatus] = useState(() => ORDERS_STATUSES.filter(item => item.selected));
    const [requestEnabled, setRequestsEnabled] = useState(false);

    const [, renderWarehouseState] = useState(0);
    const warehouseStateRef = useRef(null);
    const currentOrderRowsRef = useRef(new Map());
    if (!warehouseStateRef.current) warehouseStateRef.current = createWarehousePreferenceState({
        getCompanyID: () => localStorage.getItem("currentCompanyID"), changed: () => renderWarehouseState(value => value + 1),
        request: (operation, data) => new Promise(resolve => {
            // The shared helper leaves transport failures pending; settle this local operation via onError.
            Library.makeApiRequest({ endpoint: `X/Stores/${operation}`, data, onErrorShowMsg: false,
                onError: error => resolve(error?.data?.status ? error : { data: { status: 0 } })
            }).then(resolve, () => resolve({ data: { status: 0 } }));
        })
    });
    const exportPreparationRef = useRef(new Map());
    const manualReportRunning = row => manualQueryClient.getQueryData(manualReportKey(row))?.state === "running";
    useEffect(() => {
        // Observer churn (including disabled loading cards) is not a report-state
        // change. Only a changed attempt state can change warehouse edit locks.
        const states = new Map(manualQueryClient.getQueryCache().getAll()
            .map(query => [query.queryHash, query.state.data?.state]));
        return manualQueryClient.getQueryCache().subscribe(event => {
            if (event?.type !== "queryUpdated" || event.query?.queryKey?.[0] !== "ManualCheckReport") return;
            const state = event.query.state.data?.state;
            if (states.get(event.query.queryHash) === state) return;
            states.set(event.query.queryHash, state);
            renderWarehouseState(value => value + 1);
        });
    }, [manualQueryClient]);
    const warehouseInteractionRef = useRef(null);
    warehouseInteractionRef.current = { isLoading };
    const warehouseInteractionBlocked = row => warehouseInteractionRef.current.isLoading || manualReportRunning(row) || exportBlocked(row) ||
        (checkSessionRef.current?.row?._id === row?._id && (checkSessionRef.current.processing || checkSessionRef.current.openingReport));
    const destinationState = row => {
        const preference = warehouseStateRef.current.state(row);
        return { ...preference, processing: exportAction(row)?.state === "exporting", actionBlocked: exportBlocked(row), ready: preference.ready && !exportBlocked(row), disabled: preference.disabled || !canEditDestinationWarehouse(row, warehouseInteractionBlocked(row)),
            onChange: value => {
                // Recheck the latest effective row, including polled Checking locks.
                // A callback retained by an open menu must not authorize a stale card.
                const latest = currentOrderRowsRef.current.get(row?._id);
                if (!latest || latest.storeID !== row?.storeID || latest.retailBrandID !== row?.retailBrandID ||
                    !canEditDestinationWarehouse(latest, warehouseInteractionBlocked(latest))) return;
                return warehouseStateRef.current.select(latest, value);
            } };
    };
    const observeOrderRowsRef = useRef(null);
    if (!observeOrderRowsRef.current) observeOrderRowsRef.current = rows => {
        currentOrderRowsRef.current = new Map(rows.map(row => [row._id, row]));
        for (const row of rows) {
            warehouseStateRef.current.load(row);
        }
    };
    useEffect(() => () => { exportPreparationRef.current.clear(); warehouseStateRef.current.dispose(); }, []);

    const [showOrderPreview, setShowOrderPreview] = useState(false);
    const [orderDetails, setOrderDetails] = useState(null);

    const filters = {
        retailBrandIDs: filteredRetailBrand?.map(item => item?.value),
        labelBrandIDs: filteredLabelBrand?.map(item => item?.value),
        searchText: searchText,
        // Empty organizer selection removes only the additional active-status restriction.
        // Never send []: the server interprets it as all statuses, including Archive.
        statuses: (filteredStatus.length ? filteredStatus : ORDERS_STATUSES).map(item => item.value)
    };
    const gridQueryKey = ["OrdersGrid", filters];
    const listQueryKey = ["OrdersList", filters];
    // #endregion

    // #region METHODS - MAIN WINDOW HEADER
    const onClickRefreshAction = () => {
        if (props?.layoutType === "List") AppQueryClient.invalidateQueries(listQueryKey);
        else {
            //else AppQueryClient.invalidateQueries(gridQueryKey);
            gridRef.current?.reset?.(gridQueryKey);
        }
    };

    const onSearch = text => {
        setSearchText(text);
    };

    const onRetailBrandChange = (selections, dropdownDataWithSelections) => {
        setFilteredRetailBrand(selections);
        setAllRetailBrandsList(dropdownDataWithSelections);
    };

    const onLabelBrandChange = (selections, dropdownDataWithSelections) => {
        setFilteredLabelBrand(selections);
        setAllLabelBrandsList(dropdownDataWithSelections);
    };

    const onStatusChange = selections => {
        const selected = ORDERS_STATUSES.filter(item => selections?.some(selection => selection?.value === item.value));
        if (selected.length === filteredStatus.length && selected.every((item, index) => item.value === filteredStatus[index]?.value)) return;
        setFilteredStatus(selected);
        setAllStatusList(ORDERS_STATUSES.map(item => ({ ...item, selected: selected.some(selection => selection.value === item.value) })));
    };

    const onFiltersLoaded = data => {
        //const defaultRetailBrand = sessionStorage.getItem("currentRetailBrand") || localStorage.getItem("defaultRetailBrand");
        //const defaultLabelBrand = sessionStorage.getItem("currentLabelBrand") || localStorage.getItem("defaultLabelBrand");
        if (data?.retailBrands?.length) {
            const retailBrands = data?.retailBrands?.map((item, index) => {
                const defaultRetailBrandID = localStorage.getItem("defaultRetailBrand");
                let retailBrandName = item?.name;

                if (item?._id === defaultRetailBrandID) {
                    retailBrandName = `${item?.name} (default)`;
                }
                return {
                    name: retailBrandName,
                    value: item?._id,
                    selected: true
                    //selected: item?._id === defaultRetailBrand
                };
            });
            //const defaultRetailBrandObj = retailBrands?.find(item => item?.value === defaultRetailBrand) || retailBrands?.[0];

            setAllRetailBrandsList(retailBrands);
            setFilteredRetailBrand(retailBrands);
        }

        if (data?.connectedLabelBrands?.length) {
            const labelBrands = data?.connectedLabelBrands?.map((item, index) => {
                const defaultLabelBrandID = localStorage.getItem("defaultLabelBrand");
                let labelBrandName = item?.name;

                if (item?._id === defaultLabelBrandID) {
                    labelBrandName = `${item?.name} (default)`;
                }
                return {
                    name: labelBrandName,
                    value: item?._id,
                    selected: true
                    //selected: item?._id === defaultLabelBrand
                };
            });
            //const defaultLabelBrandObj = labelBrands?.find(item => item?.value === defaultLabelBrand) || labelBrands?.[0];

            setAllLabelBrandsList(labelBrands);
            setFilteredLabelBrand(labelBrands);
        }

        setRequestsEnabled(true);
    };
    // #endregion

    // #region METHODS - CONFIRMATION DIALOGS
    // UI context guards supplement the existing server authorization; they grant no authority.
    const exportContextKey = (row, warehouse) => JSON.stringify([
        row?._id, row?.retailBrandID, row?.storeID, row?.status, row?.updatedAt, row?.supplierOrderType,
        row?.retailBrandPosStatus, row?.storePosStatus, warehouse?.id,
        localStorage.getItem("currentCompanyID"), filters
    ]);
    const latestExportContextKeyRef = useRef(null);
    latestExportContextKeyRef.current = exportContextKey;
    const isCurrentExportWorkflow = session => {
        if (!session || (session.intent === "manual-check" ? exportWorkflowRef.current !== session : exportAction(session.row)?.session !== session)) return false;
        const row = currentOrderRowsRef.current.get(session.row?._id) || automaticRowsRef.current.get(session.row?._id) || session.row;
        const current = !isChecking(row) && (session.intent === "manual-check"
            ? canStartManualCheck(row, getMappedStatus(row?.status)) : ButtonsEnabledRules("export", row?.status)) &&
            exportContextKey(row, session.warehouse) === session.contextKey;
        if (!current || session.intent === "manual-check") return current;
        const destination = warehouseStateRef.current.state(row);
        return !manualReportRunning(row) && destination.ready && destination.scopeKey === session.scopeKey && String(destination.warehouse?.id) === String(session.warehouse?.id);
    };
    const latestWorkflowGuardRef = useRef(null);
    latestWorkflowGuardRef.current = isCurrentExportWorkflow;
    const cancelFrontSystemsCheck = session => {
        if (!session?.checkContext?.reportRunID || session.cancelPending || session.cancelAttempts >= 2 ||
            session.cancelOutcome?.status === "uncertain" || (!session.processing && !session.serverActive)) return;
        const token = session.cancelToken;
        if (session.cancelOutcome && !(token && token !== session.cancelledToken &&
            (session.cancelOutcome.status === "acknowledged" && session.cancelOutcome.checkpoint === "deferred" ||
                session.cancelOutcome.status === "rejected" && session.cancelOutcome.reason === "REPORT_RUN_NOT_ACTIVE"))) return;
        session.cancelPending = true;
        session.cancelAttempts = (session.cancelAttempts || 0) + 1;
        session.cancelledToken = token;
        const retainOutcome = raw => {
            const outcome = raw?.cancellation;
            session.cancelOutcome = outcome?.status === "acknowledged" && outcome.publicationFenced === true
                ? { status: "acknowledged", checkpoint: ["deferred", "acknowledged", "not-active"].includes(outcome.checkpoint) ? outcome.checkpoint : "uncertain" }
                : outcome?.status === "rejected" ? { status: "rejected", reason: outcome.reason === "REPORT_RUN_NOT_ACTIVE" ? outcome.reason : "REPORT_CANCEL_REJECTED" }
                : { status: "uncertain" };
            if (session.cancelOutcome.status !== "acknowledged" || session.cancelOutcome.checkpoint === "uncertain")
                Library.showToastMessage({ type: "error", message: session.cancelOutcome.status === "rejected"
                    ? "CHECK cancellation was rejected. Saved state must be reconciled; no CHECK was resubmitted."
                    : "CHECK cancellation or checkpoint closure is uncertain. No automatic retry was submitted." });
            manualQueryClient.invalidateQueries(session.reportKey);
        };
        // A durable publication fence can precede the checkpoint handle. At most
        // one later exact-handle cleanup is allowed after a known acknowledgement
        // or definite NOT_ACTIVE rejection; uncertainty is never retried.
        requestFrontSystemsCheck({ ...session.checkContext, checkAction: "cancel", cancelToken: session.cancelToken })
            .then(retainOutcome, () => retainOutcome(null)).finally(() => {
                session.cancelPending = false;
                if (session.cancelled && session.cancelToken && session.cancelToken !== token) cancelFrontSystemsCheck(session);
            });
    };
    const closeFrontSystemsReview = () => {
        const confirmation = exportConfirmationRef.current;
        if (confirmation && !confirmation.session.submitted) setExportAction(confirmation.row, { state: "cancelled" });
        const session = checkSessionRef.current;
        if (session) {
            session.cancelled = true;
            cancelFrontSystemsCheck(session);
        }
        checkSessionRef.current = null;
        exportWorkflowRef.current = null;
        exportConfirmationRef.current = null;
        setExportConfirmation(null);
        setFrontSystemsReview(null);
    };
    const beginFrontSystemsCheck = async (row, session, initialAction = "start") => {
        if (!isCurrentExportWorkflow(session) || session.stage !== "check" || !session.checkEnabled || row?.supplierOrderType !== "Bulk Order") return;
        // Only the explicit UPDATE callback may replace a saved result.
        if (!["start", "update"].includes(initialAction) || (initialAction === "update") !== !!session.savedReport?.available) return;
        // Connection preparation gates new calculations, never an authorized saved read.
        if (row?.retailBrandPosStatus !== "Connected") { showPOSSetupNeededDialog(); return; }
        if (row?.storePosStatus !== "Connected") { showPOSConnectionNeededDialog(row?.storeOurID); return; }
        session.contextKey = exportContextKey(row);
        session.exportRow = { ...row };
        session.stage = "review";
        session.processing = true;
        session.latestReport = null; session.cancelPending = false; session.cancelAttempts = 0;
        session.cancelOutcome = null; session.cancelledToken = null; session.cancelToken = null;
        session.reportKey = manualReportKey(row);
        manualQueryClient.setQueryData(session.reportKey, retainSavedReport(session.savedReport, { state: "running", error: null }));
        checkSessionRef.current = session;
        const context = { scopeKind: "order-check/v2", orderID: row?._id, retailBrandID: row?.retailBrandID,
            storeID: row?.storeID, expectedUpdatedAt: row?.updatedAt, supplierOrderType: row?.supplierOrderType,
            reportContract: "manual-report/v1", reportRunID: uuidv4() };
        setGenericConfirmationPopup({ show: false });
        // Selected order metadata is display-only; it grants no provider or server authority.
        const selectionSummary = { orderNumber: row?.orderNumber, brand: row?.labelBrandName,
            store: row?.storeName, seasons: Array.isArray(row?.seasons) ? row.seasons.join(", ") : undefined,
            barcodes: row?.totalBarcodes, units: row?.totalOrderProducts ?? row?.totalItems };
        session.checkContext = context;
        setFrontSystemsReview({ status: "checking", processing: true, context, selectionSummary, posDisplay: session.posDisplay, manualReport: session.savedReport });
        try {
            const current = () => !session.cancelled && checkSessionRef.current === session && isCurrentExportWorkflow(session) && session.stage === "review";
            const request = async selection => {
                const raw = await requestFrontSystemsCheck({ ...selection, checkAction: selection.checkAction === "start" ? initialAction : selection.checkAction });
                if (raw?.manualReport && current()) { session.latestReport = raw.manualReport; manualQueryClient.setQueryData(session.reportKey, previous =>
                    (previous?.generation || 0) > (raw.manualReport.generation || 0) ? previous : retainSavedReport(previous, raw.manualReport)); }
                if (typeof raw?.continuation?.cancelToken === "string" && /^[a-f0-9]{64}$/.test(raw.continuation.cancelToken))
                    session.cancelToken = raw.continuation.cancelToken;
                session.serverActive = raw?.continuation?.canContinue === true;
                // A close before the first response must still cancel once its handle arrives.
                if (session.cancelled) cancelFrontSystemsCheck(session);
                return raw;
            };
            const review = await runFullOrderCheck({ request, context, session, isCurrent: current,
                normalize: normalizeFrontSystemsCheck, onProgress: review => {
                    if (current()) setFrontSystemsReview({ ...review, processing: true, context, selectionSummary, posDisplay: review.posDisplay || session.posDisplay, manualReport: session.savedReport });
                } });
            if (current()) {
                // Final normalized merge completed before the handler/UI can become eligible.
                session.processing = false;
                const report = session.latestReport;
                const saved = report?.state === "saved" && !report.error && report.available === true && Number.isSafeInteger(report.count) && report.count >= 0;
                if (!saved && !report?.error) manualQueryClient.setQueryData(session.reportKey, previous => retainSavedReport(previous, { state: "uncertain", error: "CHECK_OR_SAVE_UNVERIFIED" }));
                const display = !saved && session.savedReview ? session.savedReview : review;
                session.savedReport = saved ? report : retainSavedReport(session.savedReport, { ...report,
                    state: report?.state && report.state !== "saved" ? report.state : "uncertain", error: report?.error || "CHECK_OR_SAVE_UNVERIFIED" });
                if (saved) session.savedReview = review;
                setFrontSystemsReview({ ...display, processing: false, context, selectionSummary,
                    posDisplay: display.posDisplay || session.posDisplay, manualReport: session.savedReport });
            }
        } catch {
            if (checkSessionRef.current === session && isCurrentExportWorkflow(session) && session.stage === "review")
                { session.processing = false;
                    session.savedReport = retainSavedReport(session.savedReport, { state: "uncertain", error: "CHECK_OR_SAVE_UNVERIFIED" });
                    manualQueryClient.setQueryData(session.reportKey, session.savedReport);
                    setFrontSystemsReview({ ...(session.savedReview || { status: "incomplete" }), processing: false, context, selectionSummary, posDisplay: session.posDisplay, manualReport: session.savedReport }); }
        }
    };
    const dispatchExistingExport = session => {
        if (session?.submitted || !isCurrentExportWorkflow(session)) return;
        session.submitted = true;
        setExportAction(session.row, { state: "exporting", session });
        if (checkSessionRef.current?.row?._id === session.row._id) { checkSessionRef.current = null; setFrontSystemsReview(null); }
        // An ambiguous result retains this order's lock. Other orders have independent sessions.
        return exportToPOS(session);
    };
    const continueExistingExport = () => {
        const session = exportWorkflowRef.current;
        if (exportBlocked(session?.row) || session?.processing || session?.cancelled || session?.invalidated || session?.stage !== "review") return;
        if (!isCurrentExportWorkflow(session)) return;
        const row = automaticRowsRef.current.get(session.row?._id) || session.row;
        if (!ButtonsEnabledRules("export", row?.status)) return;
        // Keep the saved report open when warehouse readiness or a stale callback rejects EXPORT.
        onClickExport(row, session);
    };
    useEffect(() => () => { if (checkSessionRef.current) { checkSessionRef.current.cancelled = true; cancelFrontSystemsCheck(checkSessionRef.current); } checkSessionRef.current = null; exportWorkflowRef.current = null; }, []);
    useEffect(() => {
        const session = exportWorkflowRef.current;
        if (session && !session.submitted && !isCurrentExportWorkflow(session)) closeFrontSystemsReview();
    });

    const showConfirmExportDialog = (row, warehouse, session) => {
        const confirmation = { row, warehouse, session };
        exportConfirmationRef.current = confirmation;
        setExportConfirmation(confirmation);
    };
    const closeExportConfirmation = confirmation => {
        if (exportConfirmationRef.current !== confirmation) return;
        exportConfirmationRef.current = null;
        setExportConfirmation(null);
        if (!confirmation.session.submitted) setExportAction(confirmation.row, { state: "cancelled" });
        if (exportWorkflowRef.current === confirmation.session) closeFrontSystemsReview();
    };
    const confirmExport = confirmation => {
        if (exportConfirmationRef.current !== confirmation) return;
        exportConfirmationRef.current = null;
        setExportConfirmation(null);
        dispatchExistingExport(confirmation.session);
    };

    const showPOSSetupNeededDialog = () => {
        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            show: true,
            headingText: "ATTENTION!",
            headingColor: "var(--text-icons-orange-500, #f90)",
            title: "POS setup needed!",
            message:
                "Your POS system is not yet configured to connect to BOtag.<br />A POS connection is required to export orders.",
            type: "error",
            showConfirm: true,
            showCancel: true,
            confirmText: "POS SETUP",
            cancelText: "CANCEL",
            confirmState: "secondary",
            onConfirmClicked: () => {
                navigate("/stores/pos");
            }
        });
    };

    const showPOSConnectionNeededDialog = storeOurID => {
        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            show: true,
            headingText: "ATTENTION!",
            headingColor: "var(--text-icons-orange-500, #f90)",
            title: "POS connection needed!",
            message: "BOtag is not yet connected to this store.<br />A POS connection is required to export orders.",
            type: "error",
            showConfirm: true,
            showCancel: true,
            confirmText: "CONNECT STORE",
            cancelText: "CANCEL",
            confirmState: "secondary",
            onConfirmClicked: () => {
                navigate(`/stores/edit/${storeOurID}#stock-sharing`);
                sessionStorage.setItem("scrollToPOS", true);
            }
        });
    };
    // #endregion

    // #region METHODS - BUTTON ACTIONS AND PROPS
    const closeSavedCheck = () => { savedViewerGeneration.current++; setSavedCheckReview(null); };
    const onAutomaticCheckRowsChange = rows => {
        automaticRowsRef.current = new Map(rows.map(row => [row._id, row]));
        const session = exportWorkflowRef.current;
        if (["warehouse", "confirmation"].includes(session?.stage) && !session.submitted && isChecking(automaticRowsRef.current.get(session.row?._id))) {
            closeFrontSystemsReview();
            setGenericConfirmationPopup({ show: false });
        }
    };
    const onClickSavedCheck = async row => {
        if (!canOpenSaved(row)) return;
        const generation = ++savedViewerGeneration.current;
        const response = await Library.makeApiRequest({ endpoint: "SupplierOrders/getSupplierOrderCheckResult",
            data: { supplierOrderID: row._id, retailBrandID: row.retailBrandID, storeID: row.storeID }, onErrorShowMsg: false });
        const saved = response?.data?.status === 200 ? response.data.data : null;
        if (generation === savedViewerGeneration.current && saved?.available && saved.evidence?.version === "saved-store-check/v1")
            setSavedCheckReview(saved.evidence.review);
    };
    useEffect(() => () => { savedViewerGeneration.current++; }, []);
    const onClickPreview = row => {
        if (isChecking(row)) return;
        setOrderDetails(row);
        setShowOrderPreview(true);
    };

    const onClickManualCheck = async selected => {
        const currentRow = automaticRowsRef.current.get(selected?._id) || selected;
        const row = { ...currentRow, posDisplay: selected?.posDisplay || currentRow?.posDisplay };
        if (!canStartManualCheck(row, getMappedStatus(row?.status)) || exportBlocked(row) ||
            checkSessionRef.current?.processing || exportWorkflowRef.current?.resolvingWarehouse || exportWorkflowRef.current?.openingReport) return;
        closeFrontSystemsReview();
        const session = { row, intent: "manual-check", contextKey: exportContextKey(row), stage: "review", checkEnabled: true,
            openingReport: true, posDisplay: row.posDisplay };
        exportWorkflowRef.current = session;
        checkSessionRef.current = session;
        const context = manualReportContext(row), selectionSummary = { orderNumber: row.orderNumber, brand: row.labelBrandName, store: row.storeName, barcodes: row.totalBarcodes, units: row.totalOrderProducts ?? row.totalItems };
        setFrontSystemsReview({ status: "loading-report", processing: true, context, selectionSummary, posDisplay: row.posDisplay });
        const knownReport = manualQueryClient.getQueryData(manualReportKey(row));
        try {
            const report = await readManualReport(row);
            if ((knownReport?.available && !report.available) || report.generation < (knownReport?.generation || 0))
                throw Error("SAVED_REPORT_REGRESSED");
            if (session.cancelled || checkSessionRef.current !== session || !isCurrentExportWorkflow(session)) return;
            session.openingReport = false;
            manualQueryClient.setQueryData(manualReportKey(row), report);
            session.savedReport = report;
            session.posDisplay = report.posDisplay || session.posDisplay;
            if (report.available) {
                const review = savedReportReview(report, context, selectionSummary, session.posDisplay);
                session.savedReview = review;
                setFrontSystemsReview(review);
            } else if (["running", "uncertain"].includes(report.state)) {
                setFrontSystemsReview({ status: "incomplete", context, selectionSummary, posDisplay: row.posDisplay,
                    message: "A prior CHECK has an unresolved outcome. No new CHECK started.", manualReport: report });
            } else { session.stage = "check"; beginFrontSystemsCheck(row, session); }
        } catch {
            session.openingReport = false;
            if (checkSessionRef.current === session && !session.cancelled) setFrontSystemsReview({ status: "incomplete", context, selectionSummary,
                posDisplay: row.posDisplay, message: "Saved CHECK status unavailable. No CHECK started.", manualReport: { error: "REPORT_READ_FAILED" } });
        }
    };

    const updateManualCheck = () => {
        const session = checkSessionRef.current;
        if (!session?.savedReport?.available || ["running", "uncertain"].includes(session.savedReport.state) || session.processing || session.openingReport || session.cancelled || !isCurrentExportWorkflow(session)) return;
        session.stage = "check";
        session.invalidated = false;
        beginFrontSystemsCheck(automaticRowsRef.current.get(session.row._id) || session.row, session, "update");
    };

    const onClickExport = async (selectedRow, sourceSession) => {
        const row = currentOrderRowsRef.current.get(selectedRow?._id) || automaticRowsRef.current.get(selectedRow?._id) || selectedRow;
        if (isChecking(row) || manualReportRunning(row) || exportBlocked(row) ||
            (checkSessionRef.current?.row?._id === row?._id && checkSessionRef.current.processing)) return;
        if (sourceSession && (exportWorkflowRef.current !== sourceSession || !isCurrentExportWorkflow(sourceSession))) return;
        if (!ButtonsEnabledRules("export", row?.status)) return;
        if (row?.retailBrandPosStatus !== "Connected") { showPOSSetupNeededDialog(); return; }
        if (row?.storePosStatus !== "Connected") { showPOSConnectionNeededDialog(row?.storeOurID); return; }
        const selected = destinationState(row);
        if (!selected.ready) return;
        const preparation = { orderID: row._id, contextKey: exportContextKey(row), owner: exportWorkflowRef.current };
        exportPreparationRef.current.set(selected.orderKey, preparation);
        setExportAction(row, { state: "preparing" });
        try {
            // A read reconciles server-owned binding/default before immutable capture; never retries an export.
            const loaded = await warehouseStateRef.current.load(row, true);
            const latest = currentOrderRowsRef.current.get(row._id) || automaticRowsRef.current.get(row._id) || row;
            if (!loaded || exportPreparationRef.current.get(selected.orderKey) !== preparation ||
                latestExportContextKeyRef.current(latest) !== preparation.contextKey || isChecking(latest) || manualReportRunning(latest) ||
                !ButtonsEnabledRules("export", latest.status) || latest.retailBrandPosStatus !== "Connected" || latest.storePosStatus !== "Connected" ||
                (sourceSession && !latestWorkflowGuardRef.current(sourceSession))) return;
            const current = warehouseStateRef.current.state(latest);
            if (!current.ready || current.scopeKey !== selected.scopeKey || current.value !== selected.value) return;
            const warehouse = Object.freeze({ ...current.warehouse });
            const session = { row: latest, intent: "export", warehouse, contextKey: latestExportContextKeyRef.current(latest, warehouse),
                scopeKey: current.scopeKey, preferenceRevision: current.revision, companyID: localStorage.getItem("currentCompanyID"), orderKey: current.orderKey, stage: "confirmation", checkEnabled: false,
                exportRow: Object.freeze({ ...latest }), exportWarehouse: warehouse, onSuccessClosePopup: false };
            closeFrontSystemsReview();
            exportWorkflowRef.current = session;
            setExportAction(row, { state: "confirmation", session });
            showConfirmExportDialog(session.exportRow, session.exportWarehouse, session);
        } finally {
            if (exportPreparationRef.current.get(selected.orderKey) === preparation) {
                exportPreparationRef.current.delete(selected.orderKey);
                if (exportAction(row)?.state === "preparing") setExportAction(row, { state: "cancelled" });
            }
        }
    };

    const exportToPOS = async session => {
        const row = session.exportRow, warehouse = session.exportWarehouse;
        const payload = { retailBrandID: row?.retailBrandID, storeID: row?.storeID,
            supplierOrderIDs: [row?._id], warehouseIdFromPOS: warehouse?.id };
        const res = await new Promise(resolve => {
            Library.makeApiRequest({ endpoint: "X/SupplierOrders/exportSupplierOrderToPOS", data: payload, onErrorShowMsg: false,
                onError: error => resolve(error?.data?.status ? error : error?.response?.data?.status ? error.response : null) }).then(resolve, () => resolve(null));
        });
        const result = res?.data?.data, orders = result?.supplierOrdersExportResult, products = result?.productsExportResult;
        const succeeded = res?.data?.status === 200 && orders?.createdCount === 1;
        // A transport loss or malformed/non-completion reply cannot authorize a retry or a default save.
        const unknown = !succeeded && result?.exportOutcome !== "not-dispatched";
        setExportAction(row, { state: succeeded ? "succeeded" : unknown ? "unknown" : "failed", session });
        const outcome = { row, state: succeeded ? "success" : unknown ? "unknown" : "failed",
            productsExported: Number.isSafeInteger(products?.barcodesExported) && products.barcodesExported >= 0 ? products.barcodesExported : null,
            productsCreated: Number.isSafeInteger(products?.createdCount) && products.createdCount >= 0 ? products.createdCount : null };
        const visible = () => localStorage.getItem("currentCompanyID") === session.companyID;
        if (visible()) { setExportOutcomes(items => [...items, outcome]); onClickRefreshAction(); }
        if (succeeded) {
            // Export success is presented immediately. Preference persistence cannot hold it hostage.
            const saved = await warehouseStateRef.current.saveAfterExport(row, { companyID: session.companyID,
                scopeKey: session.scopeKey, revision: session.preferenceRevision, warehouseID: warehouse.id });
            if (!saved.saved && visible()) {
                const message = saved.outcome === "unknown"
                    ? "The order was exported. Saving the warehouse default has an unknown outcome; it was not retried."
                    : "The order was exported, but the warehouse default was not saved. No EXPORT was repeated.";
                setExportOutcomes(items => items.map(item => item === outcome ? { ...item, preferenceWarning: message } : item));
                Library.showToastMessage({ type: "error", message });
            }
        }
    };

    const onClickSetRules = item => {
        if (isChecking(item)) return;
        navigate(`/products/pos-export?labelBrandID=${item?.labelBrandID}`);
    };

    const onClickDelete = item => {
        if (isChecking(item)) return;
        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            useV2: true,
            useIconSrc: AlertIcon(),
            headingText: "ATTENTION!!",
            headingColor: "var(--text-icons-orange-500)",
            title: "Deleting order?",
            messageHtml:
                "This order will be permanently removed from the Orders page. You will lose access to it from your account.",
            confirmText: "YES",
            cancelText: "NO",
            confirmState: "secondary",
            confirmTextColor: "var(--text-icons-green-500)",
            onConfirmClicked: () => {
                deleteSupplierOrder(item, false);
            }
        });
    };

    const deleteSupplierOrder = async (item, forceDelete = false) => {
        const res = await Library.makeApiRequest({
            endpoint: "SupplierOrders/deleteBulkOrder",
            data: { supplierOrderID: item?._id, forceDelete: forceDelete },
            setLoader: setIsLoading,
            onErrorShowMsg: false
        });

        if (res?.status !== 200) return;

        Library.showToastMessage({ message: "Order deleted successfully", type: "success" });

        onClickRefreshAction();
    };

    // #endregion

    // #region LIST COMPONENT PROPS
    const listColumns = [
        {
            name: "Order nr.",
            selector: row => row?.orderNumber ?? "--"
        },
        {
            name: "From brand",
            selector: row => row?.labelBrandName ?? "--"
        },
        {
            name: "To store",
            selector: row => row?.storeName ?? "--"
        },
        {
            name: "Imported on",
            selector: row => formatDefaultDateTime(row?.createdAt)
        },
        {
            name: "Imported from",
            selector: row => "Email" //row?.importedFromPosName ?? "--"
        },
        {
            name: "Total",
            selector: row => formatPrice(row?.totalPriceIncludingVAT, "kr", "--")
        }
    ];
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setViewLayoutStyle(props?.layoutType);

        setupHeaders(
            props,
            onClickRefreshAction,
            onSearch,
            onFiltersLoaded,
            allRetailBrandsList,
            onRetailBrandChange,
            filteredRetailBrand,
            allLabelBrandsList,
            onLabelBrandChange,
            filteredLabelBrand,
            allStatusList,
            onStatusChange,
            filteredStatus
        );
    }, [props?.layoutType, allRetailBrandsList, filteredRetailBrand, allLabelBrandsList, filteredLabelBrand, allStatusList, filteredStatus]);
    // #endregion

    // Presentation derived from existing individual sessions; never a global business lock.
    const visibleExportSessions = [...exportActionsRef.current.values()].filter(action =>
        action.state === "exporting" && action.session?.companyID === localStorage.getItem("currentCompanyID"));

    // #region RENDER
    return (
        <StyledPage className="orders-page">
            <FullScreenLoader isLoading={isLoading} />
            {exportConfirmation && <PremiumDialog title="Ready to export?" accessibleName="Ready to export?" premiumOverlay compact
                onClose={() => closeExportConfirmation(exportConfirmation)}>
                <p>You’re about to export Order #{exportConfirmation.row?.orderNumber} with {exportConfirmation.row?.totalBarcodes ?? "--"} barcodes.</p>
                <dl>
                    <dt>Brand</dt><dd>{exportConfirmation.row?.labelBrandName ?? "--"}</dd>
                    <dt>To Store</dt><dd>{exportConfirmation.row?.storeName ?? "--"}</dd>
                    <dt>Warehouse</dt><dd>{exportConfirmation.warehouse?.name ?? "--"}</dd>
                </dl>
                <p>If any items don’t already exist in your system, they’ll be created automatically.<br />
                    Stock will remain pending until delivery.</p>
                <div className="flex justify-content-center gap-16px flex-wrap">
                    <PremiumHeaderButton label="CANCEL" onClick={() => closeExportConfirmation(exportConfirmation)} />
                    <PremiumHeaderButton label="EXPORT" onClick={() => confirmExport(exportConfirmation)} />
                </div>
            </PremiumDialog>}

            {visibleExportSessions.length > 0 && <PremiumExportProgress orderNumber={visibleExportSessions[0].session.exportRow.orderNumber} />}
            {exportOutcomes[0] && <PremiumStatusDialog
                variant={exportOutcomes[0].state === "success" ? "success" : "warning"}
                title={exportOutcomes[0].state === "success" ? "This order is now moved to ARCHIVE" :
                    exportOutcomes[0].state === "unknown" ? "Export outcome unknown" : "Order not exported!"}
                accessibleName={exportOutcomes[0].state === "success" ? "Export completed" : "Export warning"}
                onClose={() => setExportOutcomes(items => items.slice(1))}>
                <p>Order #{exportOutcomes[0].row.orderNumber}</p>
                {exportOutcomes[0].state === "success" ? <>
                    <p>{exportOutcomes[0].productsExported ?? "Unreported number of"} products exported.<br />
                        {exportOutcomes[0].productsCreated ?? "Unreported number of"} products created.</p>
                    <p>(if products already exist in your system we don’t create them again)</p>
                    <p>Stock pending delivery.</p>
                    {exportOutcomes[0].preferenceWarning && <p role="status">{exportOutcomes[0].preferenceWarning}</p>}
                </> : <p>{exportOutcomes[0].state === "unknown"
                    ? "Server completion is unknown. No automatic retry or warehouse-default save was made. Reconcile this order before another EXPORT. Contact support@botag.no for assistance."
                    : "There was a problem exporting this order. Several causes may have prevented this order to go through. Try again in a few minutes or contact support@botag.no for assistance."}</p>}
            </PremiumStatusDialog>}

            <FullScreenOverlay show={showOrderPreview}>
                <OrderPreview onCloseClick={() => setShowOrderPreview(false)} orderDetails={orderDetails} />
            </FullScreenOverlay>

            {savedCheckReview && <FrontSystemsChangePopup mode="saved-pre-export" review={savedCheckReview} onClose={closeSavedCheck} />}
            {frontSystemsReview && <FrontSystemsChangePopup review={frontSystemsReview} onClose={closeFrontSystemsReview}
                onUpdate={updateManualCheck} updateEligible={frontSystemsReview?.manualReport?.available === true && !["running", "uncertain"].includes(frontSystemsReview?.manualReport?.state)}
                onExport={continueExistingExport} exportEligible={!!isCurrentExportWorkflow(exportWorkflowRef.current) && !exportWorkflowRef.current?.invalidated &&
                    ButtonsEnabledRules("export", exportWorkflowRef.current?.row?.status) && !manualReportRunning(exportWorkflowRef.current?.row) &&
                    destinationState(exportWorkflowRef.current?.row).ready}
                exportPending={exportBlocked(exportWorkflowRef.current?.row)} processing={exportWorkflowRef.current?.processing === true} />}

            {viewLayoutStyle === "Grid" && (
                <InfinityGridOrdersCards
                    onAutomaticCheckRowsChange={onAutomaticCheckRowsChange}
                    onCheckClick={onClickManualCheck}
                    ref={gridRef}
                    manualReports
                    usePremiumSummary
                    minColumnWidth="288px"
                    limit={12}
                    queryKey={gridQueryKey}
                    filters={filters}
                    requestEnabled={requestEnabled}
                    onDeleteClick={onClickDelete}
                    onViewClick={onClickPreview}
                    onExportClick={onClickExport}
                    destinationState={destinationState}
                    onRowsLoaded={observeOrderRowsRef.current}
                    onRulesClick={onClickSetRules}
                />
            )}

            {viewLayoutStyle === "List" && (
                <ListComponent
                    title="Orders"
                    columns={listColumns}
                    api={{
                        endpoint: "getBulkOrdersData",
                        formData: {
                            companyID: localStorage.getItem("currentCompanyID"),
                            limit: listNumRows,
                            filters: filters
                        },
                        useCommonPostRequest: true,
                        commonContext: "SupplierOrders",
                        onSuccess: data => {
                            const items = data?.result ?? [];
                            automaticRowsRef.current = new Map(items.map(item => [item._id, item]));
                            observeOrderRowsRef.current(items);
                            const mappedItems = items?.map(item => {
                                return { ...item, mappedStatus: getMappedStatus(item?.status) };
                            });
                            return mappedItems;
                        }
                    }}
                    pageLimit={listNumRows}
                    currentPage={1}
                    queryKey={listQueryKey}
                    actions={{
                        includeActions: true,
                        extraActions: [
                            {
                                title: "Delete",
                                onClick: onClickDelete,
                                isDisabled: item => isChecking(item) || !ButtonsEnabledRules("delete", item?.status)
                            },
                            {
                                title: "View",
                                onClick: onClickPreview,
                                isDisabled: item => isChecking(item) || !ButtonsEnabledRules("view", item?.status)
                            },
                            {
                                title: "Rules",
                                onClick: onClickSetRules,
                                isDisabled: item => isChecking(item) || !ButtonsEnabledRules("rules", item?.status)
                            },
                            {
                                title: "Export",
                                onClick: onClickExport,
                                isDisabled: item => isChecking(item) || manualReportRunning(item) || !ButtonsEnabledRules("export", item?.status) || !destinationState(item).ready
                            }
                        ]
                    }}
                    includeStatus={{
                        stickyStatus: true,
                        statusFromPath: "mappedStatus",
                        statusColors: orderStatusColors,
                        width: "220px",
                        renderStatus: ({ row, defaultStatusView }) => (
                            <div className="flex align-items-center gap-4px">
                                {defaultStatusView}
                                <OrderMissingSizesTooltip data={row} placement="left" />
                            </div>
                        )
                    }}
                    requestEnabled={requestEnabled}
                    onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
                />
            )}
        </StyledPage>
    );
    // #endregion
}

// #region HEADERS
function setupHeaders(
    props,
    onClickRefreshAction,
    onSearch,
    onFiltersLoaded,
    allRetailBrandsList,
    onRetailBrandChange,
    filteredRetailBrand,
    allLabelBrandsList,
    onLabelBrandChange,
    filteredLabelBrand,
    allStatusList,
    onStatusChange,
    filteredStatus
) {
    props.setControlBarLeftBtns([
        { customSearch: <CustomSearch enabled={true} onTextChange={onSearch} placeholder="Order number, brand" /> }
    ]);

    props.setControlBarCenterBtns([
        { to: "/orders", active: true, disabled: false, title: "ORDERS" },
        { to: "/orders/reorders", active: false, disabled: false, title: "REORDERS" },
        { to: "/orders/archive", active: false, disabled: false, title: "ARCHIVE" }
    ]);

    props.setControlBarRightBtns([
        { to: "/orders", active: true, disabled: false, title: "View", icon: "eye" },
        { to: "/orders/calendar", active: false, disabled: true, title: "Calendar", icon: "calendar" },
        { to: "/orders/details", active: false, disabled: true, title: "Details", icon: "microscope" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <OrdersHeader
                key={window.location.pathname}
                visible={true}
                onRefreshClick={onClickRefreshAction}
                layoutTypeOptions={{
                    gridEnabled: true,
                    listEnabled: true
                }}
                firstOrganize={{
                    visible: true,
                    dropdownData: allLabelBrandsList,
                    onDropdownChange: onLabelBrandChange,
                    disabled: allLabelBrandsList?.length === 1,
                    type: allLabelBrandsList?.length === 1 ? "singleSelect" : "multiSelect",
                    multidropdownOptions: {
                        selectAllText: "Select all label brands",
                        labelAllSelected: "All label brands",
                        labelNoneSelected: "--",
                        allItems: allLabelBrandsList
                    }
                }}
                secondOrganize={{
                    visible: true,
                    dropdownData: allStatusList,
                    onDropdownChange: onStatusChange,
                    type: "multiSelect",
                    multidropdownOptions: {
                        selectAllText: "Select all status",
                        labelAllSelected: "All status",
                        labelNoneSelected: "--",
                        allItems: allStatusList
                    }
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useRetailBrands: true,
                        useConnectedLabelBrands: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                search={{
                    visible: false
                }}
                settingsButton={{
                    visible: true
                }}
            />
        )
    });
}
// #endregion

// #region EXPORTED FUNCTIONS

export const getMappedStatus = status => {
    const statusObject = DEFAULT_STATUSES_FILTER.find(item => item.value?.toLowerCase() === status?.toLowerCase());
    if (statusObject) return statusObject.name;
    return "--";
};

export const getOrderSpanWithColor = (status, isArchivePage = false) => {
    const mappedStatus = isArchivePage && status?.toLowerCase() === "deleted" ? "Deleted" : getMappedStatus(status);

    return getSpanWithColor(mappedStatus, orderStatusColors);
};
// #endregion

// #region SMALL COMPONENTS
// #endregion

// Match Archive's guard: child initialization can notify an empty selection
// before the organizer has initialized its selected options.
const OrdersHeader = props => {
    const statusFilterReadyRef = useRef(false);
    useEffect(() => {
        statusFilterReadyRef.current = true;
        return () => { statusFilterReadyRef.current = false; };
    }, []);
    const onStatusChange = (selections, dropdownDataWithSelections) => {
        if (!statusFilterReadyRef.current) return;
        props.secondOrganize.onDropdownChange(selections, dropdownDataWithSelections);
    };
    return <MainWindowHeader {...props} premium secondOrganize={{ ...props.secondOrganize, onDropdownChange: onStatusChange }} />;
};
